import type { ReactNode } from "react";
import { motion } from "framer-motion";
import {
  Bot,
  Bug,
  CheckCircle2,
  ClipboardList,
  Download,
  FileText,
  Folder,
  GitBranch,
  Lightbulb,
  GitPullRequest,
  Layers3,
  Paintbrush,
  Play,
  SearchCheck,
  ShieldCheck,
  Sparkles,
  Telescope,
  Terminal,
  TreePine,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { HelpFlow, HelpFlowTone } from "./help-flows";

export const FLOW_ICON_MAP: Record<string, LucideIcon> = {
  Terminal,
  FileText,
  Folder,
  GitBranch,
  Lightbulb,
  Play,
  CheckCircle2,
  Paintbrush,
  GitPullRequest,
  Download,
  Bug,
  ClipboardList,
  Telescope,
};

export const HELP_TONE_STYLES = {
  violet: {
    badge: "bg-violet-500/15 text-violet-400",
    soft: "bg-violet-500/10",
    icon: "bg-violet-500/20 text-violet-400",
    progress: "bg-violet-500",
    ring: "hover:border-violet-500/30 focus-visible:ring-violet-500/30",
    hero: "from-violet-500/10 via-transparent to-indigo-500/10",
    muted: "text-violet-400",
  },
  blue: {
    badge: "bg-blue-500/15 text-blue-400",
    soft: "bg-blue-500/10",
    icon: "bg-blue-500/20 text-blue-400",
    progress: "bg-blue-500",
    ring: "hover:border-blue-500/30 focus-visible:ring-blue-500/30",
    hero: "from-blue-500/10 via-transparent to-cyan-500/10",
    muted: "text-blue-400",
  },
  emerald: {
    badge: "bg-emerald-500/15 text-emerald-400",
    soft: "bg-emerald-500/10",
    icon: "bg-emerald-500/20 text-emerald-400",
    progress: "bg-emerald-500",
    ring: "hover:border-emerald-500/30 focus-visible:ring-emerald-500/30",
    hero: "from-emerald-500/10 via-transparent to-lime-500/10",
    muted: "text-emerald-400",
  },
  amber: {
    badge: "bg-amber-500/15 text-amber-400",
    soft: "bg-amber-500/10",
    icon: "bg-amber-500/20 text-amber-400",
    progress: "bg-amber-500",
    ring: "hover:border-amber-500/30 focus-visible:ring-amber-500/30",
    hero: "from-amber-500/10 via-transparent to-orange-500/10",
    muted: "text-amber-400",
  },
  rose: {
    badge: "bg-rose-500/15 text-rose-400",
    soft: "bg-rose-500/10",
    icon: "bg-rose-500/20 text-rose-400",
    progress: "bg-rose-500",
    ring: "hover:border-rose-500/30 focus-visible:ring-rose-500/30",
    hero: "from-rose-500/10 via-transparent to-pink-500/10",
    muted: "text-rose-400",
  },
} satisfies Record<
  HelpFlowTone,
  {
    badge: string;
    soft: string;
    icon: string;
    progress: string;
    ring: string;
    hero: string;
    muted: string;
  }
>;

function FloatingOrb({ className }: { className: string }) {
  return <div className={cn("absolute rounded-full blur-3xl", className)} />;
}

function IllustrationTile({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className={cn(
        "rounded-2xl border border-white/[0.08] bg-white/[0.04] p-5 shadow-lg shadow-black/20 backdrop-blur-xl",
        className
      )}
    >
      {children}
    </motion.div>
  );
}

function StepBadge({
  flow,
  stepIndex,
  totalSteps,
}: {
  flow: HelpFlow;
  stepIndex: number;
  totalSteps: number;
}) {
  const styles = HELP_TONE_STYLES[flow.tone];

  return (
    <div className="absolute left-6 top-6 flex items-center gap-2 rounded-full border border-white/[0.1] bg-white/[0.08] px-3 py-1.5 text-xs font-semibold text-foreground/80 backdrop-blur-xl">
      <span className={cn("rounded-full px-2 py-0.5", styles.badge)}>Step {stepIndex + 1}</span>
      <span>{flow.steps[stepIndex]?.title ?? flow.title}</span>
      <span className="text-muted-foreground/70">/</span>
      <span>{totalSteps}</span>
    </div>
  );
}

function StoryMarkers({ labels, tone }: { labels: string[]; tone: HelpFlowTone }) {
  const styles = HELP_TONE_STYLES[tone];

  return (
    <div className="flex flex-wrap gap-2">
      {labels.map((label) => (
        <span
          key={label}
          className={cn(
            "rounded-full border border-white/[0.08] bg-white/[0.06] px-3 py-1 text-xs font-medium text-muted-foreground",
            styles.soft
          )}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

function HeroCard({
  icon: Icon,
  title,
  detail,
  tone,
  className,
}: {
  icon: LucideIcon;
  title: string;
  detail: string;
  tone: HelpFlowTone;
  className?: string;
}) {
  const styles = HELP_TONE_STYLES[tone];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: [0, -6, 0] }}
      transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
      className={cn(
        "absolute rounded-2xl border border-white/[0.08] bg-white/[0.06] p-4 shadow-lg shadow-black/20 backdrop-blur-xl",
        className
      )}
    >
      <div className="flex items-center gap-3">
        <div className={cn("flex h-11 w-11 items-center justify-center rounded-2xl", styles.icon)}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{detail}</p>
        </div>
      </div>
    </motion.div>
  );
}

export function HelpCenterHeroIllustration() {
  return (
    <div className="relative h-56 overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03] shadow-inner shadow-black/20">
      <FloatingOrb className="-left-10 top-8 h-32 w-32 bg-violet-500/10" />
      <FloatingOrb className="right-6 top-4 h-28 w-28 bg-blue-500/10" />
      <FloatingOrb className="bottom-2 left-1/3 h-24 w-24 bg-emerald-500/10" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(139,92,246,0.16),transparent_45%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.12),transparent_35%)]" />

      <HeroCard
        icon={Terminal}
        title="Get your environment ready"
        detail="Install the pieces that let Copilot CLI talk to SpecTree."
        tone="violet"
        className="left-6 top-8 w-60"
      />
      <HeroCard
        icon={FileText}
        title="Shape great epic requests"
        detail="Turn rough ideas into clear proposals your team can review."
        tone="blue"
        className="right-8 top-12 w-60"
      />
      <HeroCard
        icon={GitBranch}
        title="Plan and execute with confidence"
        detail="Move from request to plan to implementation with one guided flow."
        tone="emerald"
        className="left-24 bottom-10 w-64"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: [1, 1.04, 1] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-8 right-12 flex h-20 w-20 items-center justify-center rounded-2xl border border-white/[0.08] bg-primary text-primary-foreground shadow-lg shadow-black/20"
      >
        <TreePine className="h-10 w-10" />
      </motion.div>
    </div>
  );
}

function SetupScene({ flow, stepIndex }: { flow: HelpFlow; stepIndex: number }) {
  const activeIndex = Math.min(stepIndex, 5);

  return (
    <div className="grid h-full gap-4 lg:grid-cols-[1.2fr_0.8fr]">
      <IllustrationTile className="flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Environment story
            </p>
            <p className="mt-1 text-lg font-semibold text-foreground">From access to verification</p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-3xl border border-white/[0.08] bg-gray-900 text-white shadow-lg shadow-black/20">
            <Terminal className="h-6 w-6" />
          </div>
        </div>
        <div className="rounded-2xl bg-gray-900 p-5 text-white shadow-lg shadow-black/20">
          <div className="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-2 w-2 rounded-full bg-rose-400" />
            <span className="h-2 w-2 rounded-full bg-amber-400" />
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            <span className="ml-3">specTree-setup.sh</span>
          </div>
          <div className="space-y-2 font-mono text-sm">
            <div className="text-violet-300">gh auth refresh -s read:packages</div>
            <div className="text-blue-300">npm config set @ttc-spectree:registry ...</div>
            <div className="text-emerald-300">@spectree list epics</div>
          </div>
        </div>
        <StoryMarkers
          labels={["Access", "Registry", "Token", "Install", "Verify"]}
          tone={flow.tone}
        />
      </IllustrationTile>
      <div className="grid gap-3">
        {[
          "Choose your platform",
          "Confirm prerequisites",
          "Refresh package access",
          "Connect npm to GitHub Packages",
          "Install the skill pack",
          "Verify the connection",
        ].map((label, index) => (
          <IllustrationTile
            key={label}
            className={cn(
              "flex items-center gap-3 p-3",
              index === activeIndex ? "border-violet-500/30 shadow-lg shadow-black/20" : "opacity-80"
            )}
          >
            <div
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-2xl text-sm font-semibold",
                index === activeIndex ? "bg-violet-600 text-white" : "bg-white/[0.06] text-muted-foreground"
              )}
            >
              {index + 1}
            </div>
            <p className="text-sm font-medium text-foreground/80">{label}</p>
          </IllustrationTile>
        ))}
      </div>
    </div>
  );
}

function RequestScene({ flow }: { flow: HelpFlow }) {
  return (
    <div className="grid h-full gap-4 lg:grid-cols-[1.05fr_0.95fr]">
      <IllustrationTile className="flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Narrative arc
            </p>
            <p className="mt-1 text-lg font-semibold text-foreground">
              Tell the story before building
            </p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-3xl bg-blue-500/20 text-blue-400">
            <FileText className="h-6 w-6" />
          </div>
        </div>
        <div className="space-y-3 rounded-2xl border border-blue-500/20 bg-blue-500/10 p-5">
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-400">
              Problem
            </p>
            <p className="mt-2 text-sm text-foreground/80">
              What is slowing the team down or blocking customer value?
            </p>
          </div>
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-400">
              Proposal
            </p>
            <p className="mt-2 text-sm text-foreground/80">
              Describe the outcome you want, not just the implementation detail.
            </p>
          </div>
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-400">Impact</p>
            <p className="mt-2 text-sm text-foreground/80">
              Explain who benefits and how you will recognize success.
            </p>
          </div>
        </div>
      </IllustrationTile>
      <IllustrationTile className="flex flex-col justify-between">
        <div className="flex items-center gap-3 rounded-3xl border border-white/[0.08] bg-white/[0.04] px-4 py-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-3xl bg-blue-500/20 text-blue-400">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Guided by the Request Formulator</p>
            <p className="text-sm text-muted-foreground">
              Your CLI turns your answers into a structured request.
            </p>
          </div>
        </div>
        <StoryMarkers labels={["Problem", "Solution", "Impact", "Review"]} tone={flow.tone} />
      </IllustrationTile>
    </div>
  );
}

function PlanScene() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="grid w-full max-w-3xl gap-6 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-center">
        <IllustrationTile className="text-center">
          <FileText className="mx-auto h-8 w-8 text-emerald-400" />
          <p className="mt-3 text-sm font-semibold text-foreground">Approved request</p>
          <p className="mt-1 text-xs text-muted-foreground">
            A clear problem statement becomes the plan source of truth.
          </p>
        </IllustrationTile>
        <div className="hidden items-center justify-center text-white/20 md:flex">
          <GitBranch className="h-7 w-7" />
        </div>
        <IllustrationTile className="text-center">
          <Layers3 className="mx-auto h-8 w-8 text-emerald-400" />
          <p className="mt-3 text-sm font-semibold text-foreground">Structured epic</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Planner creates features, tasks, and execution order.
          </p>
        </IllustrationTile>
        <div className="hidden items-center justify-center text-white/20 md:flex">
          <ClipboardList className="h-7 w-7" />
        </div>
        <IllustrationTile className="text-center">
          <ShieldCheck className="mx-auto h-8 w-8 text-emerald-400" />
          <p className="mt-3 text-sm font-semibold text-foreground">Ready to review</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Teams verify scope, dependencies, and acceptance criteria.
          </p>
        </IllustrationTile>
      </div>
    </div>
  );
}

function ExecuteScene({ flow }: { flow: HelpFlow }) {
  return (
    <div className="grid h-full gap-4 lg:grid-cols-[1.1fr_0.9fr]">
      <IllustrationTile className="flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Execution engine
            </p>
            <p className="mt-1 text-lg font-semibold text-foreground">
              The orchestrator keeps work moving
            </p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-3xl bg-amber-500/20 text-amber-400">
            <Play className="h-6 w-6" />
          </div>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {[
            { label: "Reads the plan", icon: FileText },
            { label: "Writes code", icon: Bot },
            { label: "Updates progress", icon: CheckCircle2 },
          ].map(({ label, icon: Icon }) => (
            <div
              key={label}
              className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-4 text-center"
            >
              <Icon className="mx-auto h-7 w-7 text-amber-400" />
              <p className="mt-3 text-sm font-semibold text-foreground">{label}</p>
            </div>
          ))}
        </div>
        <div className="rounded-2xl bg-gray-900 p-4 text-white shadow-lg shadow-black/20">
          <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-2 w-2 rounded-full bg-rose-400" />
            <span className="h-2 w-2 rounded-full bg-amber-400" />
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            <span className="ml-2">Execution timeline</span>
          </div>
          <div className="h-2 rounded-full bg-white/10">
            <div className="h-2 w-2/3 rounded-full bg-amber-400" />
          </div>
        </div>
      </IllustrationTile>
      <IllustrationTile className="flex flex-col justify-between">
        <StoryMarkers labels={["Queue", "Implement", "Validate", "Track"]} tone={flow.tone} />
        <div className="space-y-3">
          {[
            "Feature picked from the execution plan",
            "Task status flips to In Progress",
            "Validations run before completion",
          ].map((label) => (
            <div
              key={label}
              className="flex items-start gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.04] px-4 py-3"
            >
              <div className="mt-0.5 h-2.5 w-2.5 rounded-full bg-amber-500" />
              <p className="text-sm text-foreground/80">{label}</p>
            </div>
          ))}
        </div>
      </IllustrationTile>
    </div>
  );
}

function ReviewScene({ flow }: { flow: HelpFlow }) {
  return (
    <div className="grid h-full gap-4 lg:grid-cols-[1fr_1fr]">
      <IllustrationTile className="flex flex-col justify-between">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              Quality pass
            </p>
            <p className="mt-1 text-lg font-semibold text-foreground">Inspect what changed and why</p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-3xl bg-rose-500/20 text-rose-400">
            <SearchCheck className="h-6 w-6" />
          </div>
        </div>
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-5">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-400">
                Files
              </p>
              <p className="mt-2 text-sm text-foreground/80">
                Review the linked code artifacts and changed paths.
              </p>
            </div>
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.04] p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-400">
                Decisions
              </p>
              <p className="mt-2 text-sm text-foreground/80">
                Understand the tradeoffs recorded during implementation.
              </p>
            </div>
          </div>
          <div className="mt-3 rounded-2xl border border-white/[0.08] bg-white/[0.04] p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-400">
              Validation
            </p>
            <p className="mt-2 text-sm text-foreground/80">
              Use acceptance criteria, test output, and follow-up tasks to tighten the result.
            </p>
          </div>
        </div>
      </IllustrationTile>
      <IllustrationTile className="flex flex-col justify-between">
        <div className="rounded-2xl bg-gray-900 p-5 text-white shadow-lg shadow-black/20">
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            Review checklist
          </p>
          <div className="mt-4 space-y-3">
            {[
              "Automated validations passed",
              "Manual edge cases reviewed",
              "Follow-up tasks captured",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 rounded-2xl bg-white/[0.06] px-4 py-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                <span className="text-sm text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <StoryMarkers labels={["Inspect", "Validate", "Iterate"]} tone={flow.tone} />
      </IllustrationTile>
    </div>
  );
}

export function HelpStepIllustration({
  flow,
  stepIndex,
  totalSteps,
}: {
  flow: HelpFlow;
  stepIndex: number;
  totalSteps: number;
}) {
  return (
    <div
      className={cn(
        "relative h-full overflow-hidden rounded-2xl bg-zinc-950/60 bg-gradient-to-br",
        HELP_TONE_STYLES[flow.tone].hero
      )}
    >
      <FloatingOrb className="-left-10 bottom-4 h-28 w-28 bg-white/[0.08]" />
      <FloatingOrb className="right-6 top-8 h-24 w-24 bg-white/[0.06]" />
      <StepBadge flow={flow} stepIndex={stepIndex} totalSteps={totalSteps} />
      <div className="relative h-full px-6 pb-6 pt-20">
        {flow.id === "cli-setup" ? <SetupScene flow={flow} stepIndex={stepIndex} /> : null}
        {flow.id === "create-request" ? <RequestScene flow={flow} /> : null}
        {flow.id === "plan-epic" ? <PlanScene /> : null}
        {flow.id === "execute" ? <ExecuteScene flow={flow} /> : null}
        {flow.id === "review" ? <ReviewScene flow={flow} /> : null}
      </div>
    </div>
  );
}
